import { Component, ElementRef, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { NgxColorsModule } from 'ngx-colors';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { PlayerMessageService } from '../../../core/services/player.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { GroupedCompetencies, PlayerTheme } from '../../consumer.entity';
import { defaultTheme, fonts } from '../../../core/types';
import { SubjectJsonModel } from '../../../subject/subject.json.model';

@Component({
  selector: 'pixl-consumer-player-config',
  templateUrl: './consumer-player-config.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgxColorsModule,
    MatTabsModule,
    MatCheckbox,
    MatButton,
    MatTooltip,
  ],
  standalone: true,
  styleUrls: ['../form-dialog-consumer.component.scss']
})
export class ConsumerPlayerConfigComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Input() consumerId?: string;
  @Input() resetTheme!: PlayerTheme;
  @Input() competencies: SubjectJsonModel[] = [];

  @ViewChild('playerPreviewIframe') playerPreviewIframe!: ElementRef<HTMLIFrameElement>;

  private playerService = inject(PlayerMessageService);
  private sanitizer = inject(DomSanitizer);

  public iframePreviewUrl!: SafeResourceUrl;
  public disabledColorSchemeBtn = signal(true);

  public groupedCompetencies: GroupedCompetencies[] = [];

  ngOnInit(): void {
    this.createResetTheme();

    if (this.consumerId) {
      const unsafeUrl = this.playerService.theme2PlayerPreviewUrl(this.consumerId);
      this.iframePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(unsafeUrl);
    }

    this.groupCompetenciesByFirstLetter();
  }

  /** Store the original/default theme for reset */
  private createResetTheme(): void {
    const themeFormGroupValue = this.parentForm?.value || {};
    this.resetTheme = { ...defaultTheme, ...themeFormGroupValue };
  }

  public setDefaultTheme(): void {
    const themeFormGroup = this.parentForm.get('theme') as FormGroup;
    if (themeFormGroup) {
      themeFormGroup.patchValue(this.resetTheme);
      this.sendThemeToPlayer();
    }
    this.disabledColorSchemeBtn.set(true);
  }

  /** Send the current theme to the player iframe */
  public sendThemeToPlayer(): void {
    if (!this.playerService.iFrameReady) return;
    const themeColors = this.parentForm.value;
    if (!themeColors) return;

    this.disabledColorSchemeBtn.set(false);

    const message = {
      themePreview: {
        themeColors: { ...themeColors }
      }
    };

    this.playerService.postMessageToPlayer(
      this.playerService.theme2PlayerPreviewUrl(this.consumerId || ''),
      message
    );
  }

  public onIframeLoaded(): void {
    if (!this.consumerId) return;

    this.playerService.iFrameReady = true;
    this.playerService.playerPreviewIFrame = this.playerPreviewIframe;

    // initialize iframe
    this.playerService.postMessageToPlayer(
      this.playerService.theme2PlayerPreviewUrl(this.consumerId),
      { themePreview: 'redaInitIframe' }
    );

    // send the current theme
    this.sendThemeToPlayer();
  }

  private groupCompetenciesByFirstLetter(): void {
    const grouped: Record<string, SubjectJsonModel[]> = {};
    const sorted = [...this.competencies].sort((a, b) => a.name.localeCompare(b.name));

    sorted.forEach(comp => {
      const letter = comp.name ? comp.name.charAt(0).toUpperCase() : '#';
      if (!grouped[letter]) grouped[letter] = [];
      grouped[letter].push(comp);
    });

    this.groupedCompetencies = Object.keys(grouped)
      .sort()
      .map(letter => ({ letter, competencies: grouped[letter] }));
  }

  public getFont(competency: SubjectJsonModel): string {
    const subjectConfigs = this.parentForm.get('playerSettings.subjectConfigs')?.value || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const config = subjectConfigs.find((c: any) => c.subjectId === competency.treeId);
    return config?.fontId ?? 'def';
  }

  public setFont(fontId: string, competency: SubjectJsonModel): void {
    const subjectConfigsFormArray = this.parentForm.get('playerSettings.subjectConfigs') as FormArray;
    if (!subjectConfigsFormArray) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const index = subjectConfigsFormArray.value.findIndex((c: any) => c.subjectId === competency.treeId);
    if (index !== -1) {
      subjectConfigsFormArray.at(index).get('fontId')?.setValue(fontId);
    }
  }

  protected readonly availableFonts = fonts;
}
